import {
  propEq,
  equals,
  isNil,
  isEmpty,
  head,
  pipe,
  filter,
  both,
  pathEq,
  prop,
  sortBy
} from 'ramda'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, batch } from 'react-redux'
import styled from 'styled-components'
import useMediaQuery from '@/hooks/use-media-query'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { toast } from '@/components/toast-notifications'
import { select } from '@/store'
import config from '@/config'
import { EditListing, ExternalLink } from '@/components/icons'
import { colors, breakpoints, fontWeights, structure } from '@/styles'
import { headKey } from '@/utils'
import Skeleton from '@/components/skeleton'
import { toUnit } from '@/utils/styles'
import ListingTypeText from '@/components/listing-type-text'
import ListingInfo from '@/features/Listing/listing-info'
import TenantMenuCards from './tenant-menu-cards'
import { showRelistModal } from './relist-modal'
import Sidebar from './tenant-dashboard-sidebar'
import TenantInfo from './tenant-info'

const { APP_PATH } = config

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px;
  background: ${colors.white};
  min-height: calc(100vh - ${toUnit(structure.header.height)});
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)});
  }
`
const AddressSection = styled.section`
  display: flex;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
  margin-bottom: 8px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 0;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 4px !important;
  }
`

const SectionHeading = styled.h1`
  font-size: 24px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 30px;
  margin: 0;
  display: flex;
`

const DashboardGrid = styled.div`
  display: grid;
  gap: 16px;
  justify-content: start;
  grid-auto-flow: row;
  width: 100%;
  max-width: 800px;

  @media screen and (min-width: 1500px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: 1500px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 1280px) {
    grid-template-columns: repeat(1, 1fr);
  }

  @media screen and (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 767px) {
    grid-template-columns: repeat(1, 1fr);
    padding: 0 32px;
  }
`

const Row = styled.div`
  display: flex;
  width: 100%;
`

const TenantInfoContainer = styled.div`
  display: flex;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 0 32px;
  }
`

const DashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  padding: 40px;
  height: 100%;
  flex: 1;
  width: max-content;
  max-width: 100%;

  @media screen and (max-width: 1500px) {
    padding: 20px 32px;
  }

  @media screen and (max-width: 767px) {
    padding: 20px 0px;
  }
`
const Col = styled(motion.div)`
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 767px) {
    padding: 0px 32px;
  }
`
const ViewListing = styled.div`
  background: ${colors.formAccent};
  border: 1px solid ${colors.formAccent};
  border-radius: 4px;
  padding: 12px 20px;
  gap: 6px;
  cursor: pointer;
  color: white;
  max-width: 250px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  transition: all 0.3s ease-in-out;
  &:hover {
    box-shadow:
      0px 6px 8px -2px rgba(29, 66, 94, 0.4),
      0px 12px 16px rgba(32, 121, 193, 0.15);
  }
`
const Relist = styled(ViewListing)`
  background: white;
  color: ${colors.formAccent};
  &:hover {
    background-color: ${colors.grey96};
    box-shadow: none;
  }
`
const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 0px 32px;
  }
`

const HeaderSpan = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-column: 1 / -1;
  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    flex-direction: column;
    align-items: flex-start;
    > div:first-of-type {
      margin-bottom: 10px;
    }
  }
`

const ListingSummary = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const isHistory = propEq('state_machine', 'history')
const equalsOne = equals('1')

const TenantDashboardHome = ({
  history,
  match: {
    params: { listingId }
  },
  contract
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const timelines = useSelector(state => select.chat.tenantTimelines(state, { listingId }))
  const tenants = Object.values(contract?.tenants || {})

  const paymentsData = useSelector(state => select.payment.listingPayments(state, { listingId }))

  const unitType = contract?.unit_type_txt_id
  const unitScope = contract?.unit_type_scope_txt_id

  useEffect(() => {
    batch(() => {
      dispatch.payment.getListingPayments({ listingId })
      dispatch.listing.getListingTimelines(listingId)
    })
  }, [listingId, dispatch.payment, dispatch.listing])

  // Find upcoming crypto payout with earliest scheduled payout date
  const closestUpcomingPayoutDate = prop(
    'payout_process_payout_after',
    head(
      pipe(
        filter(both(pathEq(['transaction_info', 'ach'], 'crypto'), propEq('processed_at', null))),
        sortBy(prop('payout_process_payout_after'))
      )(paymentsData?.approved || [])
    )
  )

  const listingAddress =
    (contract.unit_number ? `${contract.unit_number} - ` : '') + contract.address
  const contractHasEnded = isHistory(contract)
  const isMarkedAsRented = equalsOne(contract.marked_as_rented)
  const noTenants = isNil(contract.tenants) || isEmpty(contract.tenants)

  const showSkeleton = isEmpty(contract)

  const unreadCount = timelines.reduce((count, timeline) => {
    /* eslint-disable no-param-reassign */
    if (timeline.is_read === '0') {
      count++
    }
    /* eslint-enable no-param-reassign */
    return count
  }, 0)

  const duplicateData = async () => {
    const { body, error } = await dispatch.listing.cloneListing(listingId)
    if (!error) {
      const newlistingId = headKey(body.listings)
      history.replace(`/listing/create/${newlistingId}/details`)
    }
  }

  const verifyCreateListing = async () => {
    const { error, body } = await dispatch.listing.verifyCreateListing({
      unit_number: contract.unit_number,
      street_number: contract.street_number,
      zip: contract.zip
    })
    if (error) {
      return error
    }
    if (!body.listing_allowed_to_clone) {
      toast(body.message, {
        title: t('common.error', 'Error'),
        type: 'error'
      })
    }
    if (body.listing_allowed_to_clone) {
      return body.message ? showRelistModal({ listingId, history }) : duplicateData(listingId)
    }
  }

  const breadcrumbs = [
    {
      to: '/tenants',
      label: t('l.tenants.view.all_tenants.link', 'All Tenants')
    },
    { to: null, label: t('l.tenants.view.dashboard.link', 'Dashboard') }
  ]

  const showActionButtons = useMediaQuery(`(max-width: 991px)`)

  return (
    <Wrapper initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <Helmet>
        <title>{listingAddress}</title>
      </Helmet>
      <Row>
        <DashboardWrapper>
          {showSkeleton ? (
            <Skeleton width={300} height={50} />
          ) : (
            <Col>
              {!showActionButtons && <Breadcrumbs items={breadcrumbs} />}

              <AddressSection>
                <SectionHeading>{listingAddress}</SectionHeading>
              </AddressSection>

              <HeaderSpan>
                <ListingSummary>
                  {showSkeleton ? (
                    <Skeleton height={20} width={400} />
                  ) : (
                    <>
                      <ListingTypeText unitType={unitType} unitScope={unitScope} />
                      <ListingInfo listingId={listingId} hideImage hideAddress />
                    </>
                  )}
                </ListingSummary>
              </HeaderSpan>
            </Col>
          )}
          {showActionButtons && (
            <ActionButtons>
              {showSkeleton ? (
                <>
                  <Skeleton width={155} height={44} />
                  <Skeleton width={155} height={44} />
                </>
              ) : (
                <>
                  <ViewListing
                    onClick={() => window.open(`${APP_PATH}/tenant/${listingId}/details`)}
                  >
                    <ExternalLink />
                    {t('l.tenants.view.view_listing.link', 'View listing')}
                  </ViewListing>

                  <Relist onClick={() => verifyCreateListing(listingId)}>
                    <EditListing />
                    {t('l.tenants.view.relist_listing.link', 'Re-list listing')}
                  </Relist>
                </>
              )}
            </ActionButtons>
          )}

          {showSkeleton ? (
            <DashboardGrid>
              {showActionButtons ? (
                <>
                  <Skeleton height={57} />
                  <Skeleton height={57} />
                  <Skeleton height={57} />
                  <Skeleton height={57} />
                  <Skeleton height={57} />
                </>
              ) : (
                <>
                  <Skeleton height={140} width={250} />
                  <Skeleton height={140} width={250} />
                  <Skeleton height={140} width={250} />
                  <Skeleton height={140} width={250} />
                  <Skeleton height={140} width={250} />
                </>
              )}
            </DashboardGrid>
          ) : (
            <>
              <DashboardGrid>
                <TenantMenuCards
                  contract={contract}
                  unreadCount={unreadCount}
                  chatsLinkDisabled={noTenants}
                  paymentsLinkDisabled={contractHasEnded || isMarkedAsRented || noTenants}
                  contractLinkDisabled={isMarkedAsRented}
                  resourcesLinkDisabled={isMarkedAsRented}
                  payoutDate={closestUpcomingPayoutDate}
                />
              </DashboardGrid>
              {showActionButtons && (
                <TenantInfoContainer>
                  <TenantInfo tenants={tenants} contract={contract} />
                </TenantInfoContainer>
              )}
            </>
          )}
        </DashboardWrapper>

        <Sidebar
          tenants={tenants}
          contract={contract}
          listingId={listingId}
          verifyCreateListing={verifyCreateListing}
          showSkeleton={showSkeleton}
        />
      </Row>
    </Wrapper>
  )
}

export default TenantDashboardHome
